/**
 * CitationBubble Component
 *
 * Displays a citation as a grey pill showing the source name.
 * Matches Anthropic's claude.ai citation UI design.
 *
 * Features:
 * - Grey pill showing human-friendly source name (e.g., "Contract Nerds")
 * - Hover triggers tooltip with full details
 * - Click opens source URL in new tab
 * - URL is provided directly by the LLM via the url attribute
 * - With `group`, stands for several adjacent web citations: the first one's
 * name plus "+N", a paged tooltip, pinned open by click/tap/Enter
 */

import React, { useRef, useCallback } from "react";
import { useCitationTooltip } from "../../contexts/CitationTooltipContext";
import { useWebSearchResults } from "../../contexts/WebSearchResultsContext";
import type { WebCiteSource } from "../../helpers/rehypeGroupWebCites";
import { createLogger } from "../../utils/logger";
import { CitationCountBadge } from "./CitationGroupControls";
import { useCitationGroupChip } from "./hooks/useCitationGroupChip";

const log = createLogger("CitationBubble");

export interface CitationBubbleProps {
  /** Human-friendly source name (e.g., "Contract Nerds") */
  source: string;
  /** Full URL of the source (provided by LLM) */
  url?: string;
  /** The cited text content */
  children?: React.ReactNode;
  /** Optional className for styling overrides */
  className?: string;
  /** Sources of a merged run of web citations, this one first. Absent for a single citation. */
  group?: WebCiteSource[];
  /** Spliced in from Gemini's grounding; Claude's own citations leave it unset. */
  grounded?: boolean;
  /** The page's own title, from Gemini's grounding (pages read with URL context) */
  pageTitle?: string;
}

/**
 * Single citation bubble displaying source name with hover tooltip.
 */
export const CitationBubble: React.FC<CitationBubbleProps> = ({
  source,
  url,
  children,
  className = "",
  group,
  grounded = false,
  pageTitle,
}) => {
  const { state, showTooltip, hideTooltip, showTooltipGroup, pageTooltip, closeTooltip } =
    useCitationTooltip();
  const { lookupTitleByUrl } = useWebSearchResults();
  const bubbleRef = useRef<HTMLSpanElement>(null);
  // Track if we've already warned about URL lookup failure (log once per citation)
  const hasWarnedRef = useRef(false);

  const isGroup = !!group && group.length > 1;
  const isOpen = state.isVisible && !!state.group && state.group.anchorEl === bubbleRef.current;

  const openGroup = useCallback(
    (pinned: boolean) => {
      if (!bubbleRef.current || !group) return;
      showTooltipGroup(
        bubbleRef.current,
        group.map((item) => ({
          source: item.source || "Source",
          url: item.url || undefined,
          title: item.title ?? (item.url ? (lookupTitleByUrl(item.url) ?? undefined) : undefined),
        })),
        pinned
      );
    },
    [showTooltipGroup, group, lookupTitleByUrl]
  );

  const groupChipProps = useCitationGroupChip({
    isGroup,
    isOpen,
    isPinned: state.pinned,
    open: openGroup,
    close: closeTooltip,
    page: pageTooltip,
  });

  /**
   * Handle click - opens URL in new tab (only if URL is provided)
   */
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [url]
  );

  /**
   * Handle mouse enter - dispatch to global tooltip context
   * Looks up actual article title from web search results
   */
  const handleMouseEnter = useCallback(() => {
    if (isGroup) {
      openGroup(false);

      return;
    }
    const rect = bubbleRef.current?.getBoundingClientRect() ?? null;
    const title = pageTitle ?? (url ? (lookupTitleByUrl(url) ?? undefined) : undefined);

    // Log warning if URL was provided but lookup failed (only once per citation)
    if (url && !title && !hasWarnedRef.current) {
      hasWarnedRef.current = true;
      log.warn("Citation URL not found in web search results", { url, source });
    }

    showTooltip({
      anchorRect: rect,
      source,
      url,
      title,
      grounded,
    });
  }, [isGroup, openGroup, showTooltip, source, url, lookupTitleByUrl, grounded, pageTitle]);

  /**
   * Handle mouse leave - hide global tooltip
   */
  const handleMouseLeave = useCallback(() => {
    hideTooltip();
  }, [hideTooltip]);

  return (
    <span className={`citation-bubble-wrapper ${className}`} style={{ position: "relative" }}>
      {/* Render the cited text inline */}
      {children}
      {/* Citation bubble appears after the text */}
      <span
        ref={bubbleRef}
        className="citation-bubble"
        // A merged chip opens its tooltip instead, where each source has its own link
        onMouseDown={isGroup ? undefined : handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
        {...groupChipProps}
        aria-label={
          isGroup
            ? `${group?.length} citations: ${(group ?? []).map((item) => item.source).join(", ")}`
            : `Citation from ${source}`
        }
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          padding: "2px 8px",
          marginLeft: "4px",
          marginRight: "2px",
          backgroundColor: "#E9ECEF",
          borderRadius: "12px",
          fontSize: "12px",
          lineHeight: "16px",
          color: "#495057",
          cursor: url || isGroup ? "pointer" : "default",
          textDecoration: "none",
          border: "1px solid #DEE2E6",
          transition: "background-color 0.15s ease",
          whiteSpace: "nowrap",
          verticalAlign: "baseline",
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = "#DEE2E6";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = "#E9ECEF";
        }}
      >
        {source}
        {isGroup && (
          // The web pill's label is regular weight, so the count reads as part of it
          <CitationCountBadge
            count={(group?.length ?? 1) - 1}
            style={{ fontWeight: "inherit", color: "inherit" }}
          />
        )}
      </span>
    </span>
  );
};

export default CitationBubble;
